import _ from 'lodash'
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import withSizes from "react-sizes";
import { printDollarsFromCents } from "shared/utils/dollar-print";
import {
  LumenScore,
} from "manager/components/app-summary/action-bar";
import DealStatusColumn from "manager/components/DealStatusColumn";
import ROUTES from "manager/config/routes";
import DealIntegrationMark from "manager/components/IntegrationMark/DealIntegrationMark";
import { useDealsTable } from "manager/hooks/useDealsTable";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";
import Icon from "shared/components/Icon";
import Table from "shared/components/Table";
import DropdownMenu from "shared/components/DropdownMenu";
import { usePermissions, useActiveCompany } from "shared/hooks";
import { useActiveUser } from "shared/hooks/api";
import {
  COLORS,
  DEAL_UI_STATUS_CODES,
  DEAL_UI_STATUS_LABEL,
} from "shared/config/constants";
import {
  getDeConflictedDealStatusLabel,
  isVeroSubmittedApplication,
} from "shared/utils/deal-status";
import { paginationPropType } from "shared/utils/propTypes";
import { transformRoute } from "shared/utils/routing";
import { formatDate, getValueOrNA, isPlainLeftClick } from "shared/utils/ui";

import DealPropTypes from "../Deals.propTypes";
import {
  SENTILINK_FILTER_EMPTY_MESSAGE,
  shouldShowSentilinkApplicantChip,
} from "../sentilinkRiskStatus";
import {
  formatDecisionTimestamp,
  lumenRecommendationExists,
  resolveLeasingDecision,
  resolveListActionLabel,
} from "../leasingDecision";
import { resolveVero1ListState } from "../vero1ReviewStatus";
import {
  NO_PERMISSION_ROW_COPY,
  RE_RUNNING_ROW_COPY,
  REVIEW_ID_FRAUD_MENU_LABEL,
  SENTILINK_BLOCKED_EMPTY_MESSAGE,
  SHOW_BLOCKED_QUEUE_ROW_MENU,
  getBlockedByIdFraudRowState,
  idFraudReviewLocationState,
  sentilinkChipTierForApplicant,
  stuckDaysFrom,
  stuckDaysLabel,
} from "../blockedByIdFraud";

import {
  Cell,
  DealCellLink,
  FlexWrapper,
  NotAvailableSpan,
  PrimaryText,
  RowLinkTableFrame,
  SecondaryText,
  StyledCheckList,
} from "./styled";
import CosignIntegrationMark from 'manager/components/IntegrationMark/CosignIntegrationMark';
import SentilinkApplicantChip from "./SentilinkApplicantChip";

const buildApplicantCheckListItems = (
  applicants,
  showSentilinkChips,
  checkIsRunning
) =>
  (applicants || []).map((applicant) => {
    const chipTier = sentilinkChipTierForApplicant({
      riskTier: applicant.sentilinkRiskTier,
      checkIsRunning,
    });
    return {
      ...applicant,
      Icon:
        showSentilinkChips && shouldShowSentilinkApplicantChip(chipTier)
          ? () => <SentilinkApplicantChip tier={chipTier} />
          : undefined,
    };
  });

const DealsTable = ({
  deals,
  dealsCount,
  loading,
  dealStatuses,
  hasInputValue,
  setPagination,
  pagination,
  onTableChange,
  initialOrderingColumn,
  refetchDeals,
  experianPrompt,
  sentilinkRiskStatusFilter,
  isIdFraudRiskEnabled,
  blockedByIdFraudFilterActive,
}) => {
  const { page, pageSize } = pagination;
  const { activeCompany } = useActiveCompany();
  const isVero1Client = !!activeCompany?.isVero1Client;
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign);
  // VERO1 clients get the full redesigned list out of the box; the flag
  // extends the same table to non-VERO1 companies independently
  const summaryRedesignFlag = useVeroFlag(
    VeroFlag.useDealSummaryRedesignProduct1270
  );
  const listRedesignEnabled = isVero1Client || summaryRedesignFlag;
  const pmsApprovedNoVeroEnabled = useVeroFlag(
    VeroFlag.useYardiApprovedNoVeroStatus
  );
  const { canResetIdFraudRisk } = usePermissions();
  const { activeUser } = useActiveUser();
  const isInternalUser = Boolean(activeUser?.isInternal);
  const hasSentilinkRiskFilter = (sentilinkRiskStatusFilter || []).length > 0;

  const {
    getActionLink,
    goToDealSummary,
    getOtherRowEvent,
  } = useDealsTable({ refetchDeals });

  const onPaginationChange = (currentPage) => {
    const newPagination = { page: currentPage, pageSize };
    setPagination(newPagination);
  };

  const getDealStatusText = (code, complete, total, hasVeroSubmission) => {
    const isPartiallySubmitted =
      code === DEAL_UI_STATUS_CODES.partiallySubmitted;
    const dealStatus = isPartiallySubmitted
      ? DEAL_UI_STATUS_LABEL[code](complete, total)
      : DEAL_UI_STATUS_LABEL[code];
    const fallbackLabel =
      dealStatus ||
      dealStatuses.find(({ id }) => id === code)?.value ||
      "Unknown";
    return getDeConflictedDealStatusLabel(
      code,
      hasVeroSubmission,
      fallbackLabel,
      pmsApprovedNoVeroEnabled
    );
  };

  const getGuarantorStatus = (applicantsSummary) => {
    // Get all cosign applications from all applicants
    const allCosignApps = applicantsSummary
      .flatMap(applicant => applicant.cosignApplications || [])
      .filter(Boolean);
    
    if (allCosignApps.length === 0) {
      return null;
    }
    
    // Get the most recent cosign application (first one, as they're ordered by created_at desc)
    return allCosignApps[0];
  };

  const noUnitAssigned = <NotAvailableSpan>No unit assigned</NotAvailableSpan>;

  // Define columns based on feature flag
  const columns = listRedesignEnabled
    ? [
        {
          title: "Unit",
          dataIndex: "unit",
          key: "unit",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.unit,
        },
        {
          title: "Name",
          dataIndex: "applicants",
          key: "applicants",
          width: 380,
        },
        {
          title: "Date Created",
          dataIndex: "applicationDate",
          key: "applicationDate",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.applicationDate,
        },
        {
          title: "Gross Rent",
          dataIndex: "rent",
          key: "rent",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.rent,
        },
        {
          title: "Recommendation",
          dataIndex: "lumenResult",
          key: "lumenResult",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.lumenResult,
        },
        {
          title: "Leasing decision",
          dataIndex: "leasingDecision",
          key: "leasingDecision",
        },
      ]
    : useDealsListUiRedesign
    ? [
        {
          title: "ID",
          dataIndex: "dealId",
          key: "dealId",
        },
        {
          title: "Name",
          dataIndex: "applicants",
          key: "applicants",
        },
        {
          title: "Property + Unit",
          dataIndex: "unit",
          key: "unit",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.unit,
        },
        {
          title: "Date Created",
          dataIndex: "applicationDate",
          key: "applicationDate",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.applicationDate,
        },
        {
          title: "Gross Rent",
          dataIndex: "rent",
          key: "rent",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.rent,
        },
        {
          title: "Status",
          dataIndex: "dealStatus",
          key: "dealStatus",
        },
        {
          title: "Recommendation",
          dataIndex: "lumenResult",
          key: "lumenResult",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.lumenResult,
        },
      ]
    : [
        {
          title: "Unit",
          dataIndex: "unit",
          key: "unit",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.unit,
        },
        {
          title: "Name",
          dataIndex: "applicants",
          key: "applicants",
        },
        {
          title: "Date Created",
          dataIndex: "applicationDate",
          key: "applicationDate",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.applicationDate,
        },
        {
          title: "Gross Rent",
          dataIndex: "rent",
          key: "rent",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.rent,
        },
        {
          title: "Status",
          dataIndex: "dealStatus",
          key: "dealStatus",
        },
        {
          title: "Lumen Result",
          dataIndex: "lumenResult",
          key: "lumenResult",
          sorter: true,
          defaultSortOrder: initialOrderingColumn.lumenResult,
        },
      ];

  const emptyPlaceholderMessage =
    !hasInputValue && blockedByIdFraudFilterActive && isIdFraudRiskEnabled
      ? SENTILINK_BLOCKED_EMPTY_MESSAGE
      : !hasInputValue && hasSentilinkRiskFilter && isIdFraudRiskEnabled
      ? SENTILINK_FILTER_EMPTY_MESSAGE
      : undefined;

  const table = (
    <Table
      searching={hasInputValue}
      alt
      loading={loading}
      columns={columns}
      columnBreakpoints={{
        xs: {
          column1: "unit",
          column2: "applicationDate",
        }
      }}
      onRowClick={(key) => goToDealSummary(key, experianPrompt)}
      placeholder={{
        Icon: Icon.NoDealsIcon,
        label: "Applications",
        message: emptyPlaceholderMessage,
      }}
      rows={deals.map((deal, index) => {
        const applicants = deal.applicantsSummary || [];
        const applicantItems = buildApplicantCheckListItems(
          applicants,
          isIdFraudRiskEnabled,
          Boolean(
            blockedByIdFraudFilterActive && deal.sentilinkAwaitingResubmission
          )
        );
        const numTotalApplications = deal.applicantsSummary.length;
        const numCompleteApplications = deal.applicantsSummary.filter(
          (applicant) => applicant.isSubmitted
        ).length;
        // Real VERO submission signal for the de-confliction (currently submitted
        // *and* has a `submittedAt`), via the same `isVeroSubmittedApplication`
        // helper the applicant pills use. Distinct from `numCompleteApplications`
        // (isSubmitted-based) used for the partial-submission label.
        const hasVeroSubmission = deal.applicantsSummary.some((applicant) =>
          isVeroSubmittedApplication(applicant)
        );
        const isRentIcon = deal.unit?.yardiConnected && deal.lease?.rentPaid;

        const actionLink = getActionLink(deal, experianPrompt);

        const dealStatus = getDealStatusText(
          deal.dealUiStatus,
          numCompleteApplications,
          numTotalApplications,
          hasVeroSubmission
        );
        const { rent, isArchived, lastArchivedAt } = deal;

        const getDealStatus = () => (
          <DealStatusColumn
            actionLink={actionLink}
            dealUiStatus={dealStatus}
            status={dealStatus}
            isArchived={isArchived}
            lastArchivedAt={lastArchivedAt}
          />
        );

        const dealRoute = transformRoute(ROUTES.deal, { id: deal.id });
        const openDealFromAction = (event) => {
          event.stopPropagation();
          if (!isPlainLeftClick(event)) return;
          event.preventDefault();
          goToDealSummary(deal.id, experianPrompt);
        };
        const lumenScoreLabel = deal.lastLumenScore ? (!['IS', 'AI', 'APS'].includes(deal.dealUiStatus) ? deal.lastLumenScore : _.upperCase(LumenScore.STILL_CALCULATING)) : LumenScore.NOT_APPLICABLE
        const vero1ListState = deal.vero1Active ? resolveVero1ListState(deal.vero1BannerState) : null;
        const recommendationContent = vero1ListState ? (
          <>
            <PrimaryText
              data-testid={`deal-${index}-lumen-score`}
              style={{
                display: "block",
                ...(vero1ListState.subtext ? {} : { color: COLORS.secondary }),
              }}
            >
              {vero1ListState.label}
            </PrimaryText>
            {vero1ListState.subtext && (
              <SecondaryText
                data-testid={`deal-${index}-vero1-subtext`}
                style={{ display: "block" }}
              >
                {vero1ListState.subtext}
              </SecondaryText>
            )}
          </>
        ) : (
          <FlexWrapper alignItems="center">
            <PrimaryText data-testid={`deal-${index}-lumen-score`}>
              {lumenScoreLabel}
            </PrimaryText>
          </FlexWrapper>
        );
        let recommendationCell = recommendationContent;
        if (listRedesignEnabled) {
          recommendationCell = (
            <DealCellLink
              to={dealRoute}
              onClick={openDealFromAction}
              data-testid={`deal-${index}-recommendation-link`}
            >
              {recommendationContent}
            </DealCellLink>
          );
        } else if (vero1ListState) {
          recommendationCell = <div>{recommendationContent}</div>;
        }
        const recommendationExists = deal.vero1Active
          ? deal.vero1BannerState?.state === "recommendation"
          : lumenRecommendationExists(deal);
        const leasingDecision = resolveLeasingDecision({
          dealUiStatus: deal.dealUiStatus,
          approvedAt: deal.approvedAt,
          declinedAt: deal.declinedAt,
          recommendationExists,
        });
        const leasingDecisionCell = isArchived ? (
          <Cell key="leasingDecision">{getDealStatus()}</Cell>
        ) : (
          <Cell key="leasingDecision">
            <PrimaryText
              data-testid={`deal-${index}-leasing-decision`}
              style={{
                display: "block",
                ...(leasingDecision.decided ? {} : { color: COLORS.secondary }),
              }}
            >
              {leasingDecision.label}
            </PrimaryText>
            {leasingDecision.timestamp && (
              <SecondaryText
                data-testid={`deal-${index}-leasing-decision-date`}
                style={{ display: "block", whiteSpace: "nowrap" }}
              >
                {formatDecisionTimestamp(leasingDecision.timestamp)}
              </SecondaryText>
            )}
          </Cell>
        );
        const listActionLabel = resolveListActionLabel(leasingDecision);
        const latestCosignApp = getGuarantorStatus(applicants);
        const blockedRowState = blockedByIdFraudFilterActive
          ? getBlockedByIdFraudRowState({
              canResetIdFraudRisk,
              awaitingResubmission: deal.sentilinkAwaitingResubmission,
              isInternalUser,
            })
          : null;
        const stuckDays = blockedByIdFraudFilterActive
          ? stuckDaysFrom(deal.sentilinkBlockedAt)
          : null;
        const blockedReasonLine =
          blockedRowState === "re_running"
            ? RE_RUNNING_ROW_COPY
            : deal.sentilinkBlockReason;
        const blockedNameExtras = blockedByIdFraudFilterActive ? (
          <>
            {blockedReasonLine && (
              <SecondaryText data-testid={`deal-${index}-blocked-reason`}>
                {blockedReasonLine}
              </SecondaryText>
            )}
            {blockedRowState === "no_permission" && (
              <SecondaryText data-testid={`deal-${index}-blocked-permission`}>
                {NO_PERMISSION_ROW_COPY}
              </SecondaryText>
            )}
            {SHOW_BLOCKED_QUEUE_ROW_MENU && (
              <span
                onClick={(event) => event.stopPropagation()}
                data-testid={`deal-${index}-review-id-fraud`}
              >
                <DropdownMenu
                  items={[
                    {
                      key: "review-id-fraud",
                      label: REVIEW_ID_FRAUD_MENU_LABEL,
                      onClick: () =>
                        goToDealSummary(
                          deal.id,
                          experianPrompt,
                          idFraudReviewLocationState
                        ),
                    },
                  ]}
                />
              </span>
            )}
          </>
        ) : null;
        const blockedDateExtras =
          blockedByIdFraudFilterActive && stuckDays !== null ? (
            <SecondaryText data-testid={`deal-${index}-stuck-days`}>
              {stuckDaysLabel(stuckDays)}
            </SecondaryText>
          ) : null;

        const unitCell = (
          <Cell key="unit">
            {listRedesignEnabled && (
              <RouterLink
                className="deals-row-link"
                data-testid={`deal-${index}-row-link`}
                to={dealRoute}
                onClick={openDealFromAction}
                aria-label={`${listActionLabel} application`}
              />
            )}
            {deal.unit || deal.property ? (
              <>
                <PrimaryText data-testid={`deal-${index}-unitName`}>
                  {getValueOrNA(deal.unit?.name)}
                  <DealIntegrationMark
                    property={deal.property}
                    unit={deal.unit}
                    deal={deal}
                  />
                </PrimaryText>
                <SecondaryText data-testid={`deal-${index}-propertyName`}>
                  {deal.property?.name}
                </SecondaryText>
              </>
            ) : (
              noUnitAssigned
            )}
          </Cell>
        );
        const nameCell = (
          <Cell key="applicants">
            <PrimaryText data-testid={`deal-${index}-applicants`}>
              <FlexWrapper alignItems="center">
                <StyledCheckList
                  items={applicantItems}
                  $listRedesign={listRedesignEnabled}
                  onClick={() => goToDealSummary(deal.id, experianPrompt)}
                />
                {latestCosignApp && (
                  <CosignIntegrationMark
                    status={latestCosignApp?.status}
                    displayStatus={latestCosignApp?.displayStatusReadable}
                  />
                )}
              </FlexWrapper>
            </PrimaryText>
            {blockedNameExtras}
          </Cell>
        );
        const dateCell = (
          <Cell key="applicationDate">
            <PrimaryText data-testid={`deal-${index}-applicationDate`}>
              {formatDate(deal.applicationDate)}
            </PrimaryText>
            {blockedDateExtras}
          </Cell>
        );
        const rentCell = (
          <Cell key="rent">
            <FlexWrapper alignItems="center">
              <PrimaryText data-testid={`deal-${index}-rent`}>
                {printDollarsFromCents(rent)}
              </PrimaryText>
              {isRentIcon && <Icon.DollarSignIcon />}
            </FlexWrapper>
          </Cell>
        );

        // Build row values based on feature flag
        const values = listRedesignEnabled
          ? [
              unitCell,
              nameCell,
              dateCell,
              rentCell,
              <Cell key="lumenResult">{recommendationCell}</Cell>,
              leasingDecisionCell,
            ]
          : useDealsListUiRedesign
          ? [
              // Deal ID column
              <Cell>
                <PrimaryText data-testid={`deal-${index}-dealId`}>
                  #{deal.id}
                </PrimaryText>
              </Cell>,
              // Name column
              <Cell>
                <PrimaryText data-testid={`deal-${index}-applicants`}>
                  <StyledCheckList
                    items={applicantItems}
                    onClick={() => goToDealSummary(deal.id, experianPrompt)}
                  />
                </PrimaryText>
                {blockedNameExtras}
              </Cell>,
              // Property + Unit column
              <Cell>
                {deal.unit || deal.property ? (
                  <>
                    <PrimaryText data-testid={`deal-${index}-unitName`}>
                      {getValueOrNA(deal.unit?.name)}
                      <DealIntegrationMark
                        property={deal.property}
                        unit={deal.unit}
                        deal={deal}
                      />
                    </PrimaryText>
                    <SecondaryText data-testid={`deal-${index}-propertyName`}>
                      {deal.property?.name}
                    </SecondaryText>
                  </>
                ) : (
                  noUnitAssigned
                )}
              </Cell>,
              // Date Created column
              <Cell>
                <PrimaryText data-testid={`deal-${index}-applicationDate`}>
                  {formatDate(deal.applicationDate)}
                </PrimaryText>
                {blockedDateExtras}
              </Cell>,
              // Gross Rent column
              <Cell>
                <FlexWrapper alignItems="center">
                  <PrimaryText data-testid={`deal-${index}-rent`}>
                    {printDollarsFromCents(rent)}
                  </PrimaryText>
                  {isRentIcon && <Icon.DollarSignIcon />}
                </FlexWrapper>
              </Cell>,
              // Status column
              <Cell>{getDealStatus()}</Cell>,
              // Recommendation column (no "Lumen" in title)
              <Cell key="lumenResult">{recommendationCell}</Cell>,
            ]
          : [
              // Original order
              unitCell,
              nameCell,
              dateCell,
              rentCell,
              <Cell key="dealStatus">{getDealStatus()}</Cell>,
              <Cell key="lumenResult">{recommendationCell}</Cell>,
            ];

        return {
          key: deal.id,
          rawData: deal,
          values,
          viewed: false,
        };
      })}
      pagination={{
        current: page,
        onChange: onPaginationChange,
        total: dealsCount,
        defaultPageSize: pageSize,
        hideOnSinglePage: true
      }}
      getOtherRowEvent={getOtherRowEvent}
      onChange={onTableChange}
    />
  );

  return listRedesignEnabled ? (
    <RowLinkTableFrame data-testid="deals-row-link-table">
      {table}
    </RowLinkTableFrame>
  ) : (
    table
  );
};

DealsTable.propTypes = {
  deals: DealPropTypes.deals,
  refetchDeals: PropTypes.func,
  loading: PropTypes.bool.isRequired,
  hasInputValue: PropTypes.bool.isRequired,
  dealsCount: PropTypes.number.isRequired,
  setPagination: PropTypes.func.isRequired,
  pagination: paginationPropType.isRequired,
  dealStatuses: DealPropTypes.dealStatuses,
  onTableChange: PropTypes.func.isRequired,
  width: PropTypes.number.isRequired,
  initialOrderingColumn: PropTypes.shape({
    unit: PropTypes.string,
    applicationDate: PropTypes.string,
    rent: PropTypes.string,
    lumenResult: PropTypes.string,
  }).isRequired,
  experianPrompt: PropTypes.bool,
  sentilinkRiskStatusFilter: PropTypes.arrayOf(PropTypes.string),
  isIdFraudRiskEnabled: PropTypes.bool,
  blockedByIdFraudFilterActive: PropTypes.bool,
};

DealsTable.defaultProps = {
  deals: [],
  dealStatuses: [],
  sentilinkRiskStatusFilter: [],
  isIdFraudRiskEnabled: false,
  blockedByIdFraudFilterActive: false,
};

export default withSizes(({ width }) => ({ width }))(DealsTable);
